import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Alert, Button, Card } from "react-bootstrap";
import { verifyJobPostingPayment } from "../../../Services/StripeService";

// Helper function to parse query parameters
const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

export default function JobPaymentSuccess() {
  const [status, setStatus] = useState("verifying");
  const [message, setMessage] = useState("");
  const [paymentDetails, setPaymentDetails] = useState(null);
  const history = useHistory();
  const query = useQuery();

  useEffect(() => {
    const sessionId = query.get("session_id");
    const userId = query.get("userId");

    if (!sessionId || !userId) {
      setStatus("error");
      setMessage("Missing payment information. Please contact support.");
      return;
    }

    const verifyPayment = async (sessionId, userId) => {
      try {
        const result = await verifyJobPostingPayment(sessionId, userId);

        if (result.success && result.paymentCompleted) {
          setStatus("success");
          setPaymentDetails(result);

          // Store payment completion in localStorage for the PostJob component
          localStorage.setItem(
            "jobPostingPaymentCompleted",
            JSON.stringify({
              sessionId: result.sessionId,
              paymentIntentId: result.paymentIntentId,
              timestamp: new Date().toISOString(),
              userId: userId,
            })
          );

          // Automatically redirect after 30 seconds
          // setTimeout(() => {
          //   history.push("/postjob");
          // }, 30000);
        } else {
          setStatus("error");
          setMessage(result.error || "Payment verification failed");
        }
      } catch (err) {
        console.error("Error verifying payment:", err);
        setStatus("error");
        setMessage("An unexpected error occurred during payment verification.");
      }
    };

    verifyPayment(sessionId, userId);
  }, [query, history]);

  const handleContinueToJobPosting = () => {
    history.push("/postjob");
  };

  return (
    <div className="container p-0">
      <div className="dashboard-wrapper">
        <Card
          className="mx-auto shadow border-0"
          style={{
            maxWidth: "600px",
            borderRadius: "1rem",
          }}
        >
          <Card.Header
            as="h4"
            className="text-white py-4 text-center"
            style={{
              background:
                status === "success"
                  ? "linear-gradient(135deg, #28a745 0%, #34ce57 100%)"
                  : status === "error"
                  ? "linear-gradient(135deg, #dc3545 0%, #e74c3c 100%)"
                  : "linear-gradient(135deg, #2660a5 0%, #4a8eda 100%)",
              borderTopLeftRadius: "1rem",
              borderTopRightRadius: "1rem",
            }}
          >
            {status === "success"
              ? "Payment Successful"
              : status === "error"
              ? "Payment Error"
              : "Verifying Payment"}
          </Card.Header>
          <Card.Body className="text-center px-md-5 py-5">
            {status === "verifying" && (
              <>
                <div className="mb-4">
                  <div
                    className="spinner-border text-primary"
                    style={{
                      width: "4rem",
                      height: "4rem",
                      color: "#2660a5 !important",
                    }}
                    role="status"
                  >
                    <span className="sr-only">Loading...</span>
                  </div>
                </div>
                <h2 className="mb-3">Verifying Your Payment</h2>
                <div
                  className="py-3 px-4 mb-4 rounded"
                  style={{ backgroundColor: "rgba(38, 96, 165, 0.05)" }}
                >
                  <p
                    className="mb-0 font-weight-bold"
                    style={{ fontSize: "16px" }}
                  >
                    Please wait while we verify your payment...
                  </p>
                </div>
                <p className="text-muted">This may take a few moments</p>
              </>
            )}

            {status === "success" && (
              <>
                <div className="mb-4 text-success">
                  <i
                    className="fas fa-check-circle"
                    style={{ fontSize: "5rem" }}
                  ></i>
                </div>
                <h2 className="mb-4">Payment Successful!</h2>

                {paymentDetails && (
                  <div
                    className="text-left p-4 mb-4 rounded"
                    style={{
                      background: "linear-gradient(to right, #f8f9fa, #e9ecef)",
                      borderLeft: "4px solid #28a745",
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span style={{ fontSize: "16px" }}>
                        Your payment has been processed successfully. You can
                        now create a new job posting.
                      </span>
                    </div>
                  </div>
                )}

                <div
                  className="py-3 px-4 mb-4 rounded"
                  style={{ backgroundColor: "rgba(40, 167, 69, 0.05)" }}
                >
                  <p className="mb-0 font-weight-bold text-success">
                    <i className="fas fa-clock mr-2"></i>
                    Automatically redirecting to the job posting form in 30
                    seconds...
                  </p>
                </div>
              </>
            )}

            {status === "error" && (
              <>
                <div className="mb-4 text-danger">
                  <i
                    className="fas fa-exclamation-circle"
                    style={{ fontSize: "5rem" }}
                  ></i>
                </div>
                <h2 className="mb-4">Payment Verification Failed</h2>
                <Alert
                  variant="danger"
                  className="mb-4 border-0 shadow-sm text-left"
                  style={{ borderRadius: "0.5rem" }}
                >
                  <Alert.Heading>Error Details</Alert.Heading>
                  <p className="mb-2">{message}</p>
                  <hr />
                  <p className="mb-0">
                    Please contact support if you believe this is an error or if
                    you were charged but are seeing this message.
                  </p>
                </Alert>
              </>
            )}

            <div className="mt-4 pt-3">
              <Button
                variant="primary"
                size="lg"
                onClick={handleContinueToJobPosting}
                disabled={status === "verifying"}
                className="px-5 py-3"
                style={{
                  background:
                    status === "verifying"
                      ? "linear-gradient(135deg, #6c757d 0%, #adb5bd 100%)"
                      : status === "success"
                      ? "linear-gradient(135deg, #28a745 0%, #34ce57 100%)"
                      : "linear-gradient(135deg, #2660a5 0%, #4a8eda 100%)",
                  borderRadius: "2rem",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.12)",
                  border: "none",
                  fontSize: "18px",
                  fontWeight: "600",
                }}
              >
                {status === "success"
                  ? "Continue to Job Posting"
                  : "Back to Job Posting"}
              </Button>
            </div>
          </Card.Body>
          <Card.Footer
            className="text-muted text-center py-4"
            style={{
              borderBottomLeftRadius: "1rem",
              borderBottomRightRadius: "1rem",
            }}
          >
            <small>
              {status === "success"
                ? "Thank you for your payment!"
                : "Need help? Contact our support team."}
            </small>
          </Card.Footer>
        </Card>
      </div>
    </div>
  );
}
